import type { EstadoPunto, TonoEstado } from "../../components/ui/patrones";

/**
 * Cómo se pinta cada estado de OT: los 13 del flujo y `ELIMINADA`.
 *
 * Vive aparte de la página porque lo consumen las cuatro vistas —tabla,
 * tarjetas, lista y kanban—: una copia por vista terminaría con el mismo estado
 * de dos colores distintos según dónde se lo mire.
 */

export const TONO: Record<string, "ok" | "aviso" | "error" | "neutral"> = {
  BORRADOR: "neutral",
  ARTE_PENDIENTE: "aviso",
  PLANIFICADA: "aviso",
  REALIZANDO_PRUEBA: "aviso",
  APROBACION_PENDIENTE: "aviso",
  POR_IMPRIMIR: "ok",
  RECHAZADA: "error",
  EN_IMPRESION: "ok",
  PAUSADA: "error",
  FINALIZADA: "ok",
  ENTREGADA: "ok",
  CERRADA: "neutral",
  CANCELADA: "error",
  ELIMINADA: "neutral",
};

/** El punto de color usa la paleta `status` de `imprenta`. */
export const PUNTO: Record<string, EstadoPunto> = {
  BORRADOR: "borrador",
  ARTE_PENDIENTE: "pendiente",
  PLANIFICADA: "pendiente",
  REALIZANDO_PRUEBA: "imprimiendo",
  APROBACION_PENDIENTE: "pendiente",
  POR_IMPRIMIR: "info",
  RECHAZADA: "error",
  EN_IMPRESION: "imprimiendo",
  PAUSADA: "aviso",
  FINALIZADA: "completado",
  ENTREGADA: "entregado",
  CERRADA: "ok",
  CANCELADA: "error",
  ELIMINADA: "borrador",
};

/** El orden del flujo, los 13 estados. */
export const ESTADOS_EN_ORDEN = [
  "BORRADOR",
  "ARTE_PENDIENTE",
  "PLANIFICADA",
  "REALIZANDO_PRUEBA",
  "APROBACION_PENDIENTE",
  "POR_IMPRIMIR",
  "RECHAZADA",
  "EN_IMPRESION",
  "PAUSADA",
  "FINALIZADA",
  "ENTREGADA",
  "CERRADA",
  "CANCELADA",
] as const;

/**
 * Lo que ofrece el filtro de estado del tablero: el flujo y `ELIMINADA`, que se
 * filtra como cualquier otro (ADR 0100 A1). Va aparte de `ESTADOS_EN_ORDEN`
 * porque el salto forzado no la ofrece (A4, B14).
 */
export const ESTADOS_DEL_FILTRO = [...ESTADOS_EN_ORDEN, "ELIMINADA"] as const;

/** Los tres grupos que `imprenta` muestra como tarjetas de estadística. */
export const GRUPOS: { clave: string; titulo: string; estados: string[] }[] = [
  {
    clave: "pendientes",
    titulo: "Pendientes",
    estados: ["BORRADOR", "ARTE_PENDIENTE", "PLANIFICADA", "APROBACION_PENDIENTE", "POR_IMPRIMIR"],
  },
  {
    clave: "produccion",
    titulo: "En producción",
    estados: ["REALIZANDO_PRUEBA", "EN_IMPRESION", "PAUSADA"],
  },
  { clave: "finalizadas", titulo: "Finalizadas", estados: ["FINALIZADA", "ENTREGADA", "CERRADA"] },
];

/**
 * El color de cada grupo en la franja de tarjetas (ADR 0094).
 *
 * Son los de `BoardStats` de `imprenta`: ámbar lo que espera, azul lo que está
 * en la máquina, verde lo que salió.
 */
export const TONO_DE_GRUPO: Record<string, TonoEstado> = {
  pendientes: "ambar",
  produccion: "azul",
  finalizadas: "verde",
};

/**
 * Las columnas del kanban: los 12 estados del flujo, **sin `CANCELADA`**.
 *
 * Es la lista de `imprenta`, y la exclusión tiene sentido: cancelar no es un
 * lugar al que se arrastra trabajo, es una acción que se toma sobre una OT. Una
 * columna "canceladas" invitaría a mandar ahí lo que molesta.
 *
 * Se muestran **todas**, tengan o no tarjetas: un kanban donde la columna
 * siguiente aparece recién cuando ya hay algo en ella no sirve para mover
 * trabajo, que es para lo único que existe un kanban.
 */
export const COLUMNAS_KANBAN = ESTADOS_EN_ORDEN.filter((e) => e !== "CANCELADA");

/**
 * Cómo se llama cada estado en la interfaz.
 *
 * Porteado **literal** de `ESTADO_LABELS` en `constants/produccion.ts` de
 * `imprenta`, con su Title Case incluido. Hasta acá la OT mostraba el valor del
 * enum tal cual —`ARTE_PENDIENTE`, `REALIZANDO_PRUEBA`— en el tablero, en el
 * kanban, en los reportes y en la cola de facturación: el guion bajo y las
 * mayúsculas son formato de base de datos, no de pantalla (ADR 0085 y 0086
 * dicen lo mismo para los códigos de entidad y para los importes).
 *
 * `CANCELADA` no está en el mapa de allá porque su enum no la tiene; acá sí, y
 * es uno de los 13 estados.
 */
const ETIQUETA_ESTADO: Record<string, string> = {
  BORRADOR: "Borrador",
  ARTE_PENDIENTE: "Arte Pendiente",
  PLANIFICADA: "Planificada",
  REALIZANDO_PRUEBA: "Realizando Prueba",
  APROBACION_PENDIENTE: "Aprobación Pendiente",
  POR_IMPRIMIR: "Por Imprimir",
  RECHAZADA: "Rechazada",
  EN_IMPRESION: "En Impresión",
  PAUSADA: "Pausada",
  FINALIZADA: "Finalizada",
  ENTREGADA: "Entregada",
  CERRADA: "Cerrada",
  CANCELADA: "Cancelada",
  ELIMINADA: "Eliminada",
};

/**
 * Un estado sin etiqueta devuelve **su código**, no un hueco: misma regla que
 * `etiquetaDeEvento()` y `nombreDeEntidad()` (ADR 0085). Que aparezca uno en
 * crudo es la señal de que el worker sumó un estado y acá falta.
 */
export function etiquetaDeEstado(estado: string): string {
  return ETIQUETA_ESTADO[estado] ?? estado;
}
